/**
 * Los clientes y su portal.
 *
 * 🚨 Luis señaló exactamente cómo NO debía ser: «activarle el portal a un
 * cliente, pero no de esa manera; tiene que estar buscando, bajando la lista,
 * eso se ve horrible».
 *
 * ⭐ Por eso NO van en orden alfabético: van **por cuántos viajes tiene
 * rodando**. El cliente con 13 camiones en la calle es el que va a usar el
 * portal mañana; el que no despacha hace meses puede esperar. Así la decisión
 * de a quién activarle está tomada apenas se abre la pantalla.
 */

import { useCallback, useEffect, useState } from 'react'
import { pedirConSesion } from './Entrar.tsx'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'
import { comoFecha } from './formato.ts'

interface Cliente {
  id: string
  nombre: string
  codigo: string | number | null
  en_calle: number | string
  entregas: number | string
  ultimo_despacho: string | null
  cuenta_id: string | null
  cuenta_estado: string | null
  personas: number | string
  /** Cuándo entró alguien del cliente por última vez. Null = nunca. */
  ultimo_ingreso: string | null
  clave_sin_cambiar: boolean | null
}

interface Recien {
  cliente: string
  usuario: string
  clave: string
  /** `false` cuando solo se le generó otra clave a un portal que ya existía. */
  reciénActivado: boolean
}

/** «hoy», «hace 5 días». Cuándo entró alguien del cliente por última vez. */
function haceCuanto(iso: string): string {
  const f = comoFecha(iso)
  if (!f) return '—'
  const dias = Math.floor((Date.now() - f.getTime()) / 86400000)
  if (dias < 1) return 'Entró hoy'
  if (dias === 1) return 'Entró ayer'
  if (dias < 31) return `Hace ${dias} ${dias === 1 ? 'día' : 'días'}`
  return `Hace ${Math.floor(dias / 30)} meses`
}

export function Clientes() {
  const [lista, setLista] = useState<Cliente[] | null>(null)
  const [busca, setBusca] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [activando, setActivando] = useState<Cliente | null>(null)
  const [nombre, setNombre] = useState('')
  const [usuario, setUsuario] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [recien, setRecien] = useState<Recien | null>(null)
  const [cambiando, setCambiando] = useState<string | null>(null)

  const cargar = useCallback(async (queBusca = '') => {
    try {
      const r = await pedirConSesion<{ clientes: Cliente[] }>(
        `/api/admin/clientes?busca=${encodeURIComponent(queBusca)}`,
      )
      setLista(r.clientes)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar')
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function activar(e: React.FormEvent) {
    e.preventDefault()
    if (!activando || guardando || !nombre.trim() || !usuario.trim()) return
    setGuardando(true)
    setError(null)
    try {
      const r = await pedirConSesion<Recien & { mensaje: string }>('/api/admin/clientes/portal', {
        method: 'POST',
        body: JSON.stringify({
          clienteId: activando.id,
          nombre: nombre.trim(),
          usuario: usuario.trim(),
        }),
      })
      setRecien({ cliente: r.cliente, usuario: r.usuario, clave: r.clave, reciénActivado: true })
      setActivando(null)
      setNombre('')
      setUsuario('')
      await cargar(busca)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo activar')
    } finally {
      setGuardando(false)
    }
  }

  async function otraClave(c: Cliente) {
    if (!c.cuenta_id || cambiando) return
    if (
      !window.confirm(
        [
          `¿Generarle otra clave a ${c.nombre}?`,
          '',
          'La que tiene ahora deja de servir en el momento.',
          'La nueva se muestra una sola vez: anótala antes de cerrar.',
        ].join(String.fromCharCode(10)),
      )
    ) {
      return
    }
    setCambiando(c.id)
    setError(null)
    try {
      const r = await pedirConSesion<{ usuario: string; clave: string; mensaje: string }>(
        `/api/admin/clientes/portal/${c.cuenta_id}/clave`,
        { method: 'POST', body: JSON.stringify({}) },
      )
      setRecien({ cliente: c.nombre, usuario: r.usuario, clave: r.clave, reciénActivado: false })
      setAviso(r.mensaje)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar')
    } finally {
      setCambiando(null)
    }
  }

  async function cambiarAcceso(c: Cliente) {
    if (!c.cuenta_id) return
    const devolver = c.cuenta_estado === 'SUSPENDIDA'
    if (
      !devolver &&
      !window.confirm(
        [
          `¿Suspenderle el portal a ${c.nombre}?`,
          '',
          'No se borra nada: sus datos y sus cumplidos siguen guardados.',
          'Puedes devolvérselo cuando quieras.',
        ].join('\n'),
      )
    ) {
      return
    }
    setCambiando(c.id)
    try {
      const r = await pedirConSesion<{ mensaje: string }>(
        `/api/admin/clientes/portal/${c.cuenta_id}/acceso`,
        { method: 'POST', body: JSON.stringify({ permitir: devolver }) },
      )
      setAviso(r.mensaje)
      await cargar(busca)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar')
    } finally {
      setCambiando(null)
    }
  }

  const n = (v: number | string) => Number(v ?? 0)
  const pag = usarPaginas(lista ?? [])

  return (
    <section>
      <div className="tit-fila">
        <h1>Clientes y portal</h1>
        <span>
          Con el portal activado, el cliente entra por su cuenta a ver sus entregas y descargar sus
          cumplidos, sin pedírselos a nadie. Van ordenados por cuántos viajes tiene rodando.
        </span>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void cargar(busca)
        }}
        className="filtros-barra"
      >
        <div className="filtros-fila">
          <div className="campo campo-ancho">
            <label htmlFor="cl-busca">Nombre del cliente o su código</label>
            <input
              id="cl-busca"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Química del Caribe · 860000896"
            />
          </div>
          <button type="submit" className="btn-buscar">
            Buscar
          </button>
          {busca && (
            <button
              type="button"
              className="btn-limpiar"
              onClick={() => {
                setBusca('')
                void cargar('')
              }}
            >
              Limpiar
            </button>
          )}
        </div>
        {/* ⛔ Los avisos van DENTRO de la caja, nunca entre los filtros y la tabla. */}
        {(error || aviso) && (
          <p className={`linea-msg ${error ? 'linea-msg-mal' : 'linea-msg-bien'}`}>
            {error ?? aviso}
          </p>
        )}
      </form>


      {/* La clave recién creada. Se muestra UNA vez, como con los conductores. */}
      {recien && (
        <div className="cli-clave">
          <p className="cli-clave-titulo">
            {recien.reciénActivado
              ? `Portal activado para ${recien.cliente}`
              : `Clave nueva para ${recien.cliente}`}
          </p>
          <div className="cli-clave-datos">
            <div>
              <span className="cli-clave-et">Usuario</span>
              <span className="cli-clave-valor num">{recien.usuario}</span>
            </div>
            <div>
              <span className="cli-clave-et">Clave</span>
              <span className="cli-clave-valor num">{recien.clave}</span>
            </div>
          </div>
          <p className="cli-clave-nota">
            Pásasela al cliente. <strong>No la vuelves a ver</strong>: si se pierde, le generas
            otra con el botón «Otra clave».
          </p>
          <button onClick={() => setRecien(null)} className="personas-suave mt-3">
            Ya la copié
          </button>
        </div>
      )}

      {/* Activar: se pide el nombre de la persona y el usuario con que entra. */}
      {activando && (
        <form onSubmit={activar} className="personas-form">
          <h3 className="text-[16px] font-semibold">Activar el portal de {activando.nombre}</h3>
          <p className="mt-1 text-[14px] text-tinta-suave">
            {/* ⛔ Aquí NO va el correo de la empresa: la gente del cliente no lo
                tiene. Es una cuenta propia con su usuario y su clave. */}
            El cliente no entra con un correo de Grupo S&amp;M. Se le crea una cuenta propia.
          </p>

          <div className="personas-campos">
            <div>
              <label htmlFor="c-nombre" className="personas-etiqueta">
                Quién va a entrar
              </label>
              <input
                id="c-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ana Restrepo · Compras"
                autoFocus
                className="personas-campo"
              />
              <p className="personas-pista">La persona del cliente que va a usar el portal.</p>
            </div>
            <div>
              <label htmlFor="c-usuario" className="personas-etiqueta">
                Usuario para entrar
              </label>
              <input
                id="c-usuario"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value.toLowerCase())}
                placeholder="anarestrepo"
                className="personas-campo"
              />
              <p className="personas-pista">Sin espacios. Con esto y su clave entra al portal.</p>
            </div>
          </div>

          <div className="mt-5 flex gap-2">
            <button
              type="submit"
              disabled={guardando || !nombre.trim() || !usuario.trim()}
              className="personas-primario"
            >
              {guardando ? 'Activando…' : 'Activar el portal'}
            </button>
            <button type="button" onClick={() => setActivando(null)} className="personas-suave">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {lista === null && !error && <p className="mt-5 text-[15px] text-tinta-suave">Cargando…</p>}

      {/*
       * 🚨 Antes esto eran 65 TARJETAS apiladas: 6,5 pantallas de scroll.
       * Y era justo lo que Luis había reclamado con nombre propio:
       *
       *   «La parte de los clientes y activarle el portal a un cliente, pero no
       *    de esa manera. Tiene que estar buscando, bajando la lista. Eso se ve
       *    horrible.»
       *
       * Yo le dejé exactamente eso. Ahora es una tabla que pagina de a 25, y
       * sigue ordenada por viajes rodando, no alfabética: el cliente con 13
       * camiones en la calle es el que va a usar el portal mañana.
       */}
      {lista && (
        <div className="tabla-caja">
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>En la calle</th>
                  <th>Entregas</th>
                  <th>Portal</th>
                  <th>Lo está usando</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {pag.enPantalla.map((c) => {
                  const suspendido = c.cuenta_estado === 'SUSPENDIDA'
                  const tiene = !!c.cuenta_id
                  return (
                    <tr key={c.id}>
                      <td>
                        <span style={{ fontWeight: 600 }}>{c.nombre}</span>
                        {c.codigo && <small className="num">Cliente {c.codigo}</small>}
                      </td>
                      <td className="num" style={{ fontWeight: n(c.en_calle) > 0 ? 600 : 400 }}>
                        {n(c.en_calle)}
                      </td>
                      <td className="num">{n(c.entregas)}</td>
                      <td>
                        {!tiene ? (
                          <span className="et et-neu">Sin portal</span>
                        ) : suspendido ? (
                          <span className="et et-esp">Suspendido</span>
                        ) : (
                          <>
                            <span className="et et-ok">Activo</span>
                            {n(c.personas) > 0 && (
                              <small>
                                {n(c.personas)} {n(c.personas) === 1 ? 'persona' : 'personas'}
                              </small>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {/*
                         * ⭐ Activar el portal es fácil; que entren es lo que
                         * cuenta. Sin esto se activan diez y nadie se entera de
                         * que ninguno entró.
                         */}
                        {!tiene ? (
                          <span style={{ color: '#8A94A6' }}>—</span>
                        ) : c.ultimo_ingreso ? (
                          <>
                            <span className="et et-ok">{haceCuanto(c.ultimo_ingreso)}</span>
                            {c.clave_sin_cambiar && <small>Sigue con la clave que le dieron</small>}
                          </>
                        ) : (
                          <span className="et et-esp">Nunca ha entrado</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!tiene ? (
                          <button
                            onClick={() => {
                              setActivando(c)
                              setRecien(null)
                            }}
                            className="btn-tabla btn-tabla-fuerte"
                          >
                            Activar portal
                          </button>
                        ) : (
                          <>
                            {/*
                             * 🚨 Si el cliente pierde la clave, sin esto el
                             * portal le queda inservible y nadie puede
                             * arreglarlo: se muestra una sola vez al activar.
                             */}
                            <button
                              onClick={() => void otraClave(c)}
                              disabled={cambiando === c.id}
                              className="btn-tabla"
                              style={{ marginRight: 6 }}
                            >
                              Otra clave
                            </button>
                            <button
                              onClick={() => void cambiarAcceso(c)}
                              disabled={cambiando === c.id}
                              className="btn-tabla"
                            >
                            {cambiando === c.id
                              ? 'Un momento…'
                              : suspendido
                                ? 'Devolver el portal'
                                : 'Suspender'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <PieDeTabla {...pag} queSon="clientes" />
        </div>
      )}

    </section>
  )
}
